/**
 * @file PermissionsContext.tsx
 * @brief Global permissions context for dynamic RBAC access control
 */

import React, { createContext, useContext, useCallback, useMemo, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import Cookies from 'js-cookie';
import { permissionService, type PagePermission, type FeaturePermission } from '@/services/api/permissionService';
import { useAppSelector } from '@/store';
import { selectIsAuthenticated } from '@/store/slices/authSlice';

interface PermissionsContextType {
  // Page access
  canAccessPage: (pageKey: string) => boolean;
  isPageAccessLoading: boolean;
  
  // Feature permissions
  hasFeaturePermission: (pageKey: string, featureKey: string, action: string) => boolean;
  isFeaturePermissionsLoading: boolean;
  
  // Combined loading state
  isPermissionsReady: boolean;
  
  // Refresh permissions
  refetchPermissions: () => void;
}

const PermissionsContext = createContext<PermissionsContextType | undefined>(undefined);

export const usePermissions = () => {
  const context = useContext(PermissionsContext);
  if (!context) {
    throw new Error('usePermissions must be used within PermissionsProvider');
  }
  return context;
};

// Hook to check page access
export const usePageAccess = (pageKey: string): boolean => {
  const { canAccessPage } = usePermissions();
  return canAccessPage(pageKey);
};

// Hook to check feature permission
export const useFeaturePermission = (
  pageKey: string,
  featureKey: string,
  action: string
): boolean => {
  const { hasFeaturePermission } = usePermissions();
  return hasFeaturePermission(pageKey, featureKey, action);
};

// Hook for action permissions (combines page + feature checks)
export const useActionPermission = (
  pageKey: string,
  featureKey: string,
  action: string
): { allowed: boolean; loading: boolean } => {
  const { canAccessPage, hasFeaturePermission, isPermissionsReady } = usePermissions();
  
  const allowed = canAccessPage(pageKey) && hasFeaturePermission(pageKey, featureKey, action);
  
  return { allowed, loading: !isPermissionsReady };
};

interface PermissionsProviderProps {
  children: React.ReactNode;
}

export const PermissionsProvider: React.FC<PermissionsProviderProps> = ({ children }) => {
  const isAuthenticated = useAppSelector(selectIsAuthenticated);
  
  // Fallback: Check cookie directly in case Redux hasn't updated yet
  const hasAuthToken = !!Cookies.get('authToken');
  const shouldFetchPermissions = isAuthenticated || hasAuthToken;

  // Debug logging
  useEffect(() => {
    console.log('[PermissionsContext] Component mounted');
  }, []);

  useEffect(() => {
    console.log('[PermissionsContext] Auth state:', {
      reduxIsAuthenticated: isAuthenticated,
      hasAuthCookie: hasAuthToken,
      shouldFetch: shouldFetchPermissions
    });
  }, [isAuthenticated, hasAuthToken, shouldFetchPermissions]);

  // Fetch page permissions for current user
  const {
    data: pagePermissions,
    isLoading: isPageAccessLoading,
    refetch: refetchPagePermissions,
    error: pagePermError,
    fetchStatus: pageFetchStatus,
  } = useQuery({
    queryKey: ['permissions', 'pages', 'me'],
    queryFn: () => {
      console.log('[PermissionsContext] ✅ Fetching page permissions API call...');
      return permissionService.getMyPagePermissions();
    },
    enabled: shouldFetchPermissions,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes (formerly cacheTime)
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });

  // Fetch feature permissions for current user
  const {
    data: featurePermissions,
    isLoading: isFeaturePermissionsLoading,
    refetch: refetchFeaturePermissions,
    error: featurePermError,
    fetchStatus: featureFetchStatus,
  } = useQuery({
    queryKey: ['permissions', 'features', 'me'],
    queryFn: () => {
      console.log('[PermissionsContext] ✅ Fetching feature permissions API call...');
      return permissionService.getMyFeaturePermissions();
    },
    enabled: shouldFetchPermissions,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });

  // Debug logging for query state
  useEffect(() => {
    console.log('[PermissionsContext] Page query state:', {
      isLoading: isPageAccessLoading,
      hasData: !!pagePermissions,
      dataLength: pagePermissions?.length,
      error: pagePermError,
      fetchStatus: pageFetchStatus,
      enabled: shouldFetchPermissions
    });
  }, [isPageAccessLoading, pagePermissions, pagePermError, pageFetchStatus, shouldFetchPermissions]);

  useEffect(() => {
    console.log('[PermissionsContext] Feature query state:', {
      isLoading: isFeaturePermissionsLoading,
      hasData: !!featurePermissions,
      dataLength: featurePermissions?.length,
      error: featurePermError,
      fetchStatus: featureFetchStatus,
      enabled: shouldFetchPermissions
    });
  }, [isFeaturePermissionsLoading, featurePermissions, featurePermError, featureFetchStatus, shouldFetchPermissions]);

  // Refetch permissions when auth state changes from false to true (handles race condition)
  useEffect(() => {
    if (isAuthenticated && !pagePermissions && !isPageAccessLoading) {
      console.log('[PermissionsContext] 🔄 Auth state changed to true, refetching page permissions...');
      refetchPagePermissions();
    }
  }, [isAuthenticated, pagePermissions, isPageAccessLoading, refetchPagePermissions]);

  useEffect(() => {
    if (isAuthenticated && !featurePermissions && !isFeaturePermissionsLoading) {
      console.log('[PermissionsContext] 🔄 Auth state changed to true, refetching feature permissions...');
      refetchFeaturePermissions();
    }
  }, [isAuthenticated, featurePermissions, isFeaturePermissionsLoading, refetchFeaturePermissions]);

  // Debug logging for query results
  useEffect(() => {
    if (pagePermissions) {
      console.log('[PermissionsContext] ✅ Page permissions loaded:', pagePermissions);
    }
  }, [pagePermissions]);

  useEffect(() => {
    if (featurePermissions) {
      console.log('[PermissionsContext] ✅ Feature permissions loaded:', featurePermissions);
    }
  }, [featurePermissions]);

  // Create a map for quick page access lookup
  const pageAccessMap = useMemo(() => {
    if (!pagePermissions) return new Map<string, boolean>();
    
    const map = new Map<string, boolean>();
    pagePermissions.forEach((permission: PagePermission) => {
      map.set(permission.PAGE_KEY, permission.CAN_ACCESS);
    });
    return map;
  }, [pagePermissions]);

  // Create a map for quick feature permission lookup
  const featurePermMap = useMemo(() => {
    if (!featurePermissions) return new Map<string, boolean>();
    
    const map = new Map<string, boolean>();
    featurePermissions.forEach((permission: FeaturePermission) => {
      const key = `${permission.PAGE_KEY}:${permission.FEATURE_KEY}:${permission.ACTION}`;
      map.set(key, permission.GRANTED);
    });
    return map;
  }, [featurePermissions]);

  // Check if user can access a page
  const canAccessPage = useCallback(
    (pageKey: string): boolean => {
      // If permissions not loaded yet, deny access (will load and re-render)
      if (!pagePermissions || isPageAccessLoading) {
        return false;
      }
      
      // Check dynamic permissions
      const hasAccess = pageAccessMap.get(pageKey);
      if (hasAccess !== undefined) {
        return hasAccess;
      }
      
      // Default: deny access if no permission record exists
      return false;
    },
    [pagePermissions, pageAccessMap, isPageAccessLoading]
  );

  // Check if user has feature permission
  const hasFeaturePermission = useCallback(
    (pageKey: string, featureKey: string, action: string): boolean => {
      // If permissions not loaded yet, deny access (will load and re-render)
      if (!featurePermissions || isFeaturePermissionsLoading) {
        return false;
      }
      
      // Check dynamic permissions
      const key = `${pageKey}:${featureKey}:${action}`;
      const granted = featurePermMap.get(key);
      if (granted !== undefined) {
        return granted;
      }
      
      // Default: deny permission if no record exists
      return false;
    },
    [featurePermissions, featurePermMap, isFeaturePermissionsLoading]
  );

  // Refetch all permissions
  const refetchPermissions = useCallback(() => {
    refetchPagePermissions();
    refetchFeaturePermissions();
  }, [refetchPagePermissions, refetchFeaturePermissions]);

  // Check if all permissions are ready
  const isPermissionsReady = !isPageAccessLoading && !isFeaturePermissionsLoading;

  const value: PermissionsContextType = useMemo(
    () => ({
      canAccessPage,
      isPageAccessLoading,
      hasFeaturePermission,
      isFeaturePermissionsLoading,
      isPermissionsReady,
      refetchPermissions,
    }),
    [
      canAccessPage,
      isPageAccessLoading,
      hasFeaturePermission,
      isFeaturePermissionsLoading,
      isPermissionsReady,
      refetchPermissions,
    ]
  );

  return <PermissionsContext.Provider value={value}>{children}</PermissionsContext.Provider>;
};
